import { useState } from "react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { Plus, Pencil, Trash2, X } from "lucide-react"
import { useTranslation } from "react-i18next"
import * as Dialog from "@radix-ui/react-dialog"
import { useForm } from "react-hook-form"
import { api, ApiRequestError } from "@/lib/api"
import { cn } from "@/lib/utils"
import type { AccessCodeResponse } from "@fohet/caravan-booker-shared/types"
import { AccessCodeType } from "@fohet/caravan-booker-shared/constants"
import { useAdminAreal } from "@/hooks/useAdminAreal"
import { inputClass, labelClass } from "@/lib/styleClasses"

const CODE_TYPES = Object.values(AccessCodeType)

const typeColor: Record<string, string> = {
    gate: "bg-slate-800 text-white",
    toilet: "bg-blue-100 text-blue-700",
    shower: "bg-cyan-100 text-cyan-700",
    wifi: "bg-violet-100 text-violet-700",
    kitchen: "bg-orange-100 text-orange-700",
}

interface AccessCodeFormValues {
    type: string
    code: string
    description: string
    validFrom: string
    validTo: string
}

const defaultValues: AccessCodeFormValues = {
    type: "gate",
    code: "",
    description: "",
    validFrom: "",
    validTo: "",
}

interface AccessCodeFormModalProps {
    open: boolean
    onClose: () => void
    editing: AccessCodeResponse | null
    arealId: string
    queryKey: unknown[]
}

const AccessCodeFormModal = ({ open, onClose, editing, arealId, queryKey }: AccessCodeFormModalProps) => {
    const { t } = useTranslation()
    const queryClient = useQueryClient()

    const { register, handleSubmit, reset, formState: { errors } } = useForm<AccessCodeFormValues>({
        defaultValues: editing
            ? {
                type: editing.type,
                code: editing.code,
                description: editing.description ?? "",
                validFrom: editing.validFrom ?? "",
                validTo: editing.validTo ?? "",
            }
            : defaultValues,
    })

    const mutation = useMutation({
        mutationFn: (values: AccessCodeFormValues) => {
            const body = {
                type: values.type,
                code: values.code,
                description: values.description || undefined,
                validFrom: values.validFrom || undefined,
                validTo: values.validTo || undefined,
            }
            if (editing) {
                return api.patch(`/areals/${arealId}/access-codes/${editing.id}`, body)
            }
            return api.post(`/areals/${arealId}/access-codes`, body)
        },
        onSuccess: () => {
            toast.success(editing ? t("admin.accessCodes.toastUpdated") : t("admin.accessCodes.toastCreated"))
            queryClient.invalidateQueries({ queryKey })
            reset(defaultValues)
            onClose()
        },
        onError: (err) => {
            toast.error(err instanceof ApiRequestError ? err.message : t("admin.accessCodes.toastFailed"))
        },
    })

    return (
        <Dialog.Root open={open} onOpenChange={(o) => { if (!o) { reset(defaultValues); onClose() } }}>
            <Dialog.Portal>
                <Dialog.Overlay className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50" />
                <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-md bg-white rounded-[40px] shadow-2xl p-8 outline-none">
                    <div className="flex items-center justify-between mb-6">
                        <Dialog.Title className="text-xl font-black text-slate-900">
                            {editing ? t("admin.accessCodes.editTitle") : t("admin.accessCodes.addTitle")}
                        </Dialog.Title>
                        <Dialog.Close className="rounded-2xl p-2 hover:bg-slate-100 transition-colors">
                            <X className="size-5 text-slate-400" />
                        </Dialog.Close>
                    </div>

                    <form onSubmit={handleSubmit((v) => mutation.mutate(v))} className="space-y-4">
                        <div>
                            <label className={labelClass}>{t("admin.accessCodes.fieldType")}</label>
                            <select
                                {...register("type", { required: true })}
                                className={inputClass}
                            >
                                {CODE_TYPES.map((type) => (
                                    <option key={type} value={type}>
                                        {t(`admin.accessCodes.type.${type}`)}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className={labelClass}>{t("admin.accessCodes.fieldCode")}</label>
                            <input
                                {...register("code", { required: true })}
                                placeholder={t("admin.accessCodes.fieldCodePlaceholder")}
                                className={cn(inputClass, errors.code && "border-red-300 focus:ring-red-200")}
                            />
                        </div>
                        <div>
                            <label className={labelClass}>{t("admin.accessCodes.fieldDescription")}</label>
                            <input
                                {...register("description")}
                                placeholder={t("admin.accessCodes.fieldDescriptionPlaceholder")}
                                className={inputClass}
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className={labelClass}>{t("admin.accessCodes.fieldValidFrom")}</label>
                                <input
                                    type="date"
                                    {...register("validFrom")}
                                    className={inputClass}
                                />
                            </div>
                            <div>
                                <label className={labelClass}>{t("admin.accessCodes.fieldValidTo")}</label>
                                <input
                                    type="date"
                                    {...register("validTo")}
                                    className={inputClass}
                                />
                            </div>
                        </div>

                        <div className="flex items-center justify-end gap-3 pt-2">
                            <Dialog.Close asChild>
                                <button type="button" className="px-5 py-2.5 rounded-[28px] bg-slate-100 text-slate-600 font-bold text-sm hover:bg-slate-200 transition-colors">
                                    {t("admin.accessCodes.cancel")}
                                </button>
                            </Dialog.Close>
                            <button
                                type="submit"
                                disabled={mutation.isPending}
                                className="px-6 py-2.5 rounded-[28px] bg-brand text-white font-black text-sm hover:bg-brand-dark transition-colors active:scale-95 disabled:opacity-50 shadow-lg"
                            >
                                {mutation.isPending ? t("admin.accessCodes.saving") : t("admin.accessCodes.save")}
                            </button>
                        </div>
                    </form>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    )
}

export const AdminAccessCodesPage = () => {
    const { t } = useTranslation()
    const { data: areal, isLoading: arealLoading } = useAdminAreal()
    const queryClient = useQueryClient()
    const [modalOpen, setModalOpen] = useState(false)
    const [editing, setEditing] = useState<AccessCodeResponse | null>(null)

    const queryKey = ["admin", "access-codes", areal?.id]

    const { data: codes = [], isLoading } = useQuery({
        queryKey,
        queryFn: () => api.get<AccessCodeResponse[]>(`/areals/${areal!.id}/access-codes`),
        enabled: !!areal?.id,
    })

    const deleteMutation = useMutation({
        mutationFn: (id: string) => api.delete(`/areals/${areal!.id}/access-codes/${id}`),
        onSuccess: () => {
            toast.success(t("admin.accessCodes.toastDeleted"))
            queryClient.invalidateQueries({ queryKey })
        },
        onError: (err) => {
            toast.error(err instanceof ApiRequestError ? err.message : t("admin.accessCodes.toastDeleteFailed"))
        },
    })

    if (arealLoading || isLoading) {
        return <div className="text-slate-400 font-bold py-8 text-center">{t("common.loading")}</div>
    }

    if (!areal) {
        return <div className="text-red-500 font-bold py-8 text-center">{t("admin.accessCodes.noAreal")}</div>
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-black tracking-tight text-slate-900">{t("admin.accessCodes.title")}</h1>
                    <p className="text-slate-500 font-bold mt-1">{t("admin.accessCodes.subtitle")}</p>
                </div>
                <button
                    onClick={() => { setEditing(null); setModalOpen(true) }}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-[28px] bg-brand text-white font-black text-sm hover:bg-brand-dark transition-colors active:scale-95 shadow-xl"
                >
                    <Plus className="size-4" />
                    {t("admin.accessCodes.addNew")}
                </button>
            </div>

            {codes.length === 0 ? (
                <div className="text-center py-16 text-slate-400 font-bold">
                    {t("admin.accessCodes.empty")}
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {codes.map((code) => (
                        <div
                            key={code.id}
                            className="bg-white rounded-[28px] border border-slate-100 shadow-xl p-6"
                        >
                            <div className="flex items-start justify-between mb-4">
                                <span className={cn(
                                    "px-3 py-1 rounded-xl text-xs font-black uppercase tracking-wider",
                                    typeColor[code.type] ?? "bg-slate-100 text-slate-600",
                                )}>
                                    {t(`admin.accessCodes.type.${code.type}`, { defaultValue: code.type })}
                                </span>
                                <div className="flex items-center gap-1">
                                    <button
                                        onClick={() => { setEditing(code); setModalOpen(true) }}
                                        className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors text-slate-400 hover:text-slate-600"
                                    >
                                        <Pencil className="size-3.5" />
                                    </button>
                                    <button
                                        onClick={() => {
                                            if (window.confirm(t("admin.accessCodes.confirmDelete"))) {
                                                deleteMutation.mutate(code.id)
                                            }
                                        }}
                                        className="p-1.5 rounded-lg hover:bg-red-50 transition-colors text-slate-400 hover:text-red-500"
                                    >
                                        <Trash2 className="size-3.5" />
                                    </button>
                                </div>
                            </div>

                            <div className="font-mono font-black text-xl text-slate-900 border-2 border-brand rounded-2xl px-4 py-3 mb-3 text-center tracking-widest bg-brand/5">
                                {code.code}
                            </div>

                            {code.description && (
                                <p className="text-sm font-bold text-slate-500 mb-2">{code.description}</p>
                            )}

                            {(code.validFrom || code.validTo) && (
                                <p className="text-xs font-bold text-slate-400">
                                    {code.validFrom && code.validTo
                                        ? `${code.validFrom} – ${code.validTo}`
                                        : code.validFrom
                                            ? `${t("admin.accessCodes.from")} ${code.validFrom}`
                                            : `${t("admin.accessCodes.until")} ${code.validTo}`}
                                </p>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {modalOpen && (
                <AccessCodeFormModal
                    open={modalOpen}
                    onClose={() => { setModalOpen(false); setEditing(null) }}
                    editing={editing}
                    arealId={areal.id}
                    queryKey={queryKey}
                />
            )}
        </div>
    )
}
